import { defineStore } from "pinia"
import useUid from "@/helpers/uid.js"

const MODAL_DURATION = 7500

/**
 * Notification 'content' is an object with the following structure:
 *   {
 *     title: <string>,
 *     message: <string>,
 *     action: <function>,
 *     actionLabel: <string>,
 *   }
 * Only the title field is required. The actionLabel field defaults to a string
 * defined in the notification components if an action is specified.
 *
 * The components that display notifications are:
 * - ErrorComponent
 * - ToastComponent
 * - BannerComponent
 *
 * Notifications with the same key will be grouped together. These will be
 * displayed as a single notification with a count of the number of occurences.
 * The contents of the first notification with the key will be displayed.
 */

export default defineStore("notificationStore", {
  state: () => ({
    notifications: {},
    keys: {},
    seenKeys: {},
  }),
  getters: {
    banner: (state) => {
      return Object.values(state.notifications).findLast(
        (notification) => notification.type === "banner",
      )?.content
    },
    errors: (state) => {
      return Object.values(state.notifications)
        .filter(
          (notification, index) => notification.type === "error" && index <= 4,
        )
        .map((notification) => notification.content)
    },
    toasts: (state) => {
      return Object.values(state.notifications)
        .filter(
          (notification, index) => notification.type === "toast" && index <= 3,
        )
        .map((notification) => notification.content)
    },
  },
  actions: {
    /**
     * Push a notification to the store's notification stack.
     * - Notifications with the same key will be grouped together, where the
     *   content of the first notification with the key will be displayed.
     * - If the 'once' parameter is true, only the first notification with the
     *   key will be pushed to the stack. Others are ignored.
     */
    push(content, type, timeout, key, once = false) {
      const uid = useUid()
      let keyObj = null
      if (key) {
        // Skip if notification with 'once' already shown before
        if (once && this.seenKeys[key]) {
          return
        }
        this.seenKeys[key] = true
        // Find or create key object
        keyObj = this.keys[key] ??= { uid }
        // Clear previous timeout
        clearTimeout(keyObj.timer)
        keyObj.timer = null
      }
      // Push notification to stack, skip if existing key
      if (!key || keyObj.uid === uid) {
        this.notifications[uid] = {
          content: { ...content, uid },
          type,
        }
      } else {
        content = this.notifications[keyObj.uid].content
        content.count = (content.count ?? 1) + 1
      }
      // Set timeout to remove notification
      if (timeout) {
        const timer = setTimeout(() => {
          this.remove(key ? keyObj.uid : uid)
        }, timeout)
        if (key) {
          keyObj.timer = timer
        }
      }
      return key ? keyObj.uid : uid
    },
    remove(uid) {
      delete this.notifications[uid]
      const key = Object.keys(this.keys).find((k) => this.keys[k].uid === uid)
      if (key) {
        delete this.keys[key]
      }
    },
    pushContentBanner(content, key, once) {
      return this.push(content, "banner", null, key, once)
    },
    pushContentError(content, key, once) {
      return this.push(content, "error", MODAL_DURATION, key, once)
    },
    pushContentToast(content, key, once) {
      return this.push(content, "toast", MODAL_DURATION, key, once)
    },
    pushBanner(title, message, key, once) {
      return this.pushContentBanner({ title, message }, key, once)
    },
    pushError(title, message, key, once) {
      return this.pushContentError({ title, message }, key, once)
    },
    pushToast(title, message, key, once) {
      return this.pushContentToast({ title, message }, key, once)
    },
  },
  persist: {
    pick: ["seenKeys"],
  },
})
